import { useRef } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

const getFocusable = (root: HTMLElement | null) =>
  root
    ? Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => {
          const style = globalThis.getComputedStyle(el);
          return style.display !== "none" && style.visibility !== "hidden";
        }
      )
    : [];

export const useFocusTrap = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const focusFirst = () => {
    const [first] = getFocusable(containerRef.current);
    first?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;

    const focusable = getFocusable(containerRef.current);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable.at(-1)!;

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onShow = () => {
    const active = document.activeElement as HTMLElement | null;
    if (active) {
      triggerRef.current = active;
    }

    // Only auto-focus first element if focus is not already within the container
    // This prevents interfering with keyboard navigation (e.g., arrow keys in radio groups)
    const isFocusInContainer = active && containerRef.current?.contains(active);

    if (!isFocusInContainer) {
      setTimeout(focusFirst, 0);
    }
  };

  const onHide = () => {
    triggerRef.current?.focus();
    triggerRef.current = null;
  };

  return {
    containerRef,
    triggerRef,
    onKeyDown,
    onShow,
    onHide,
  };
};
